import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { MedicineLanguageSelect, MedicineWorkspaceContext } from "../../../components/adminPanel/shared/MedicineWorkspace";
import { medicineLanguageDestination } from "../../../hooks/medicineLanguageNavigation.mjs";
import useMedicineReview from "../../../hooks/useMedicineReview";
import useMedicineAiRecovery from '../../../hooks/useMedicineAiRecovery';
import { discardMedicineDrafts } from "../../../hooks/useMedicineFormDraft";
import { Box, Breadcrumbs, Button, Link as MuiLink, Stack, Typography, TextField, MenuItem, Alert } from "@mui/material";
import { Link, Navigate, Route, Routes, useLocation, useParams, useNavigate } from "react-router-dom";
import ManageMedicine from "./components/ManageMedicine";
import ManageGenericCompany from "./components/ManageGenericCompany";
import ManageMedicineCategory from "./components/ManageMedicineCategory";
import SeoTags from "./components/SeoTags";
import ManageDosageForm from "./components/ManageDosageForm";
import AdvancedMedicineSettings from "./components/AdvancedMedicineSettings";
import ManageBrochure from "./components/ManageBrochure";
import ManageCollectiveMedicineAdditionalSection from "./components/ManageCollectiveMedicineAdditionalSection";
import InvoiceVariantSelection from "./components/manual-invoice/InvoiceVariantSelection";
import { colors } from "../../../theme/designTokens";

function CataloguePage() {
  const { language, entryId } = useParams();
  return <ManageMedicine routeLanguage={language} routeEntryId={entryId} />;
}
export default function MedicinesScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const base = location.pathname.split("/medicines")[0] + "/medicines";
  const tool = medicineAcMenu.slice(1).find(item => location.pathname === `${base}/tools/${item.panelName}`);
  const { langs, activeLang } = useSelector(state => state.misc);
  const { adminMedicines, medicines: storefrontMedicines } = useSelector(state => state.medicine);
  const medicines = Array.isArray(adminMedicines) && adminMedicines.length ? adminMedicines : Array.isArray(storefrontMedicines) ? storefrontMedicines : [];
  const query = new URLSearchParams(location.search);
  const segments = location.pathname.slice(base.length).split("/").filter(Boolean);
  const routeLanguage = segments[0] !== "tools" ? segments[0] : null;
  const language = routeLanguage || query.get("language") || activeLang?.langCode || "en";
  const editing = segments[2] === "edit";
  const adding = segments[1] === "new";
  const reviewEnabled = (editing || adding) && query.get('seoReview') === '1' && Boolean(query.get('seoWorkspace'));
  const draftSessionPending = adding && !reviewEnabled && !query.get('source') && !query.get('draft');
  useEffect(() => {
    if (!draftSessionPending) return;
    const next = new URLSearchParams(location.search); next.set('draft', crypto.randomUUID());
    navigate(`${location.pathname}?${next}`, { replace: true });
  }, [draftSessionPending, location.pathname, location.search, navigate]);
  const review = useMedicineReview({ enabled: reviewEnabled, workspaceId: query.get('seoWorkspace'), generationId: query.get('seoGeneration'), language });
  const english = medicines.find(group => group.lang === 'en')?.data || [];
  const current = medicines.find(group => group.lang === language)?.data.find(row => String(row._id) === segments[1]);
  const source = query.get('source') || (language === 'en' && editing ? segments[1] : english.filter(row => row.fixedMedicineId === current?.medicineDetectorId).length === 1 ? english.find(row => row.fixedMedicineId === current?.medicineDetectorId)?._id : null);
  const reviewEnglishId = review.data?.family?.find(item => item.language === 'en')?.id;
  const baseDraftScope = source || reviewEnglishId ? `family:${source || reviewEnglishId}` : `new:${query.get('draft') || query.get('seoWorkspace') || 'manual'}`;
  const formDraftScope = reviewEnabled ? `seo:${query.get('seoWorkspace')}:${query.get('seoGeneration') || review.data?.generationId || 'pending'}` : baseDraftScope;
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const formRef = useRef({ dirty: false, busy: false });
  const setFormBusy = useCallback(value => { formRef.current.busy = value; setBusy(value); }, []);
  const resetForm = useCallback(() => { formRef.current = { dirty: false, busy: false }; setBusy(false); }, []);
  const confirmLeave = useCallback(() => {
    if (formRef.current.busy) return false;
    if (formRef.current.dirty && !window.confirm("Leave this medicine without saving your changes?")) return false;
    formRef.current.dirty = false;
    return true;
  }, []);
  const workspaceSearch = new URLSearchParams({ language }).toString();
  const toolUrl = panel => `${base}/tools/${panel}?${workspaceSearch}`;
  const catalogueQuery = new URLSearchParams(location.search);
  for (const key of ['seoWorkspace','seoGeneration','seoReview','source','draft']) catalogueQuery.delete(key);
  if (editing) catalogueQuery.set("focus", segments[1]);
  const catalogueUrl = `${base}/${language}${catalogueQuery.toString() ? `?${catalogueQuery}` : ""}`;
  const changeLanguage = nextLanguage => {
    if (nextLanguage === language) return;
    if (formRef.current.busy) return;
    let destination;
    if (reviewEnabled && !tool) {
      const next = new URLSearchParams(location.search), target = review.data?.family?.find(item => item.language === nextLanguage);
      next.delete('focus'); next.delete('source');
      if (!target && reviewEnglishId && nextLanguage !== 'en') next.set('source', reviewEnglishId);
      destination = { to: `${base}/${nextLanguage}/${target?.id ? `${target.id}/edit` : 'new'}?${next}` };
    } else if (tool) {
      const next = new URLSearchParams(location.search);
      next.set("language", nextLanguage);
      destination = { to: `${location.pathname}?${next}` };
    } else {
      destination = medicineLanguageDestination({ base, medicines, fromLanguage: language, toLanguage: nextLanguage,
        entryId: editing ? segments[1] : undefined, sourceEntryId: adding ? query.get("source") : undefined,
        adding, search: location.search });
    }
    if (destination.error) { setError(destination.error); return; }
    if (formRef.current.checkpoint) { formRef.current.checkpoint(); formRef.current.dirty = false; }
    else if (!confirmLeave()) return;
    setError("");
    navigate(destination.to);
  };
  function openSavedMedicine(discard = false) {
    if (discard && !window.confirm('Discard unsaved changes in this language only? Other language drafts will remain available.')) return;
    if (discard) {
      let actorId = ''; try { actorId = JSON.parse(localStorage.getItem('adminLoginInfo'))?._id; } catch {}
      discardMedicineDrafts({ actorId, scopes: [formDraftScope, baseDraftScope], language });
    }
    formRef.current.dirty = false;
    const next = new URLSearchParams(location.search);
    if (adding && !next.get('source') && next.get('seoWorkspace')) next.set('draft', next.get('seoWorkspace'));
    for (const key of ['seoWorkspace','seoGeneration','seoReview']) next.delete(key);
    navigate(`${location.pathname}${next.toString() ? `?${next}` : ''}`);
  }
  function acceptGenerationRetry(saved) {
    const id = saved?.contentGeneration?.requestId || saved?.contentGeneration?.id;
    if (!id || saved.id !== query.get('seoWorkspace')) return;
    const next = new URLSearchParams(location.search); next.set('seoGeneration', id);
    navigate(`${location.pathname}?${next}`, { replace: true });
  }
  const recoveryController = useMedicineAiRecovery({ workspaceId: reviewEnabled ? query.get('seoWorkspace') : null, generationId: reviewEnabled ? query.get('seoGeneration') : null,
    initialWorkspace: reviewEnabled && !review.error ? review.data?.workspace : null, beforeRetry: () => formRef.current.checkpoint?.(), onQueued: acceptGenerationRetry, onRefresh: review.refresh });
  const aiRecovery = reviewEnabled ? { workspaceId: query.get('seoWorkspace'), generationId: query.get('seoGeneration'), workspace: review.data?.workspace, reviewError: review.error,
    beforeRetry: () => formRef.current.checkpoint?.(), onQueued: acceptGenerationRetry, onRefresh: review.refresh,
    adminBase: base.split('/medicines')[0], onOpenSaved: () => openSavedMedicine(), controller: recoveryController } : null;
  const workspace = { language, languages: langs, changeLanguage, busy, formRef, setFormBusy, resetForm, confirmLeave, review: reviewEnabled ? review : null, refreshReview: review.refresh, formDraftScope, baseDraftScope, openSavedMedicine, draftSessionPending, aiRecovery };
  const legacySection = new URLSearchParams(location.search).get("section");
  if (location.pathname === base && legacySection && legacySection !== "medicines") {
    const known = medicineAcMenu.find(item => item.panelName === legacySection);
    if (known) return <Navigate replace to={`${base}/tools/${known.panelName}`} />;
  }
  return <MedicineWorkspaceContext.Provider value={workspace}><Box sx={{ width: "100%", minWidth: 0 }}>
    {error && <Alert severity="warning" onClose={() => setError("")} sx={{ mb: 2 }}>{error}</Alert>}
    <Breadcrumbs aria-label="Medicine navigation" sx={{ mb: 2 }}>
      <MuiLink component={Link} to={catalogueUrl} onClick={event => { if (!confirmLeave()) event.preventDefault(); }} underline="hover">Medicines</MuiLink>
      <Typography color="text.secondary">{tool ? tool.headPrimary : /\/new$/.test(location.pathname) ? "Add medicine" : /\/edit$/.test(location.pathname) ? "Edit medicine" : "Catalogue"}</Typography>
    </Breadcrumbs>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
    {!editing && !adding && <MedicineLanguageSelect />}
    <TextField select label="Medicine workspace" value={tool?.panelName || "medicines"} fullWidth size="small"
      onChange={e => { if (confirmLeave()) navigate(e.target.value === "medicines" ? catalogueUrl : toolUrl(e.target.value)); }} sx={{ display: { xs: "block", md: "none" }, flex: "1 1 180px", minWidth: 0 }}>
      {medicineAcMenu.map(item => <MenuItem key={item.panelName} value={item.panelName}>{item.headPrimary}</MenuItem>)}
    </TextField>
    </Box>
    <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} sx={{ display: { xs: "none", md: "flex" }, mb: 3, pb: 2, borderBottom: `1px solid ${colors.line}` }}>
      {medicineAcMenu.map((item, index) => <Button key={item.panelName} component={Link}
        size="small" variant={(!tool && index === 0) || tool === item ? "contained" : "outlined"}
        onClick={event => { if (!confirmLeave()) event.preventDefault(); }}
        to={index === 0 ? catalogueUrl : toolUrl(item.panelName)}>{index === 0 ? "Medicine catalogue" : item.headPrimary}</Button>)}
    </Stack>
    <Routes>
      <Route index element={<CataloguePage />} />
      <Route path=":language" element={<CataloguePage />} />
      <Route path=":language/new" element={<CataloguePage />} />
      <Route path=":language/:entryId/edit" element={<CataloguePage />} />
      {medicineAcMenu.slice(1).map(item => <Route key={item.panelName} path={`tools/${item.panelName}`} element={<Box sx={{ minWidth: 0 }}><Typography variant="h5" sx={{ mb: 3 }}>{item.headPrimary}</Typography>{React.cloneElement(item.component, { langFromPrev: language, workspacePage: true, key: `${item.panelName}-${language}` })}</Box>} />)}
      <Route path="*" element={<Box><Typography>Medicine page not found.</Typography><Button component={Link} to={base}>Back to medicines</Button></Box>} />
    </Routes>
  </Box></MedicineWorkspaceContext.Provider>;
}

const medicineAcMenu = [
  {
    panelName: "medicines",
    headPrimary: "Medicines",
    headSecondary: "Add/Manage Generics/ Generic Brands",
    component: <ManageMedicine />,
  },
  {
    panelName: "manage-medicine-additional-section",
    headPrimary: "Shared sections",
    headSecondary: "",
    component: <ManageCollectiveMedicineAdditionalSection />,
  },
  {
    panelName: "manage-generic-company",
    headPrimary: "Companies",
    headSecondary: "Add/Manage Generic Companies",
    component: <ManageGenericCompany />,
  },
  {
    panelName: "manage-category",
    headPrimary: "Categories",
    headSecondary: "Add/Manage Categories/ SubCategories",
    component: <ManageMedicineCategory />,
  },
  {
    panelName: "manage-dosage",
    headPrimary: "Dosage forms",
    headSecondary: "Add/Manage Dosage Forms",
    component: <ManageDosageForm />,
  },

  {
    panelName: "manage-seo-tags",
    headPrimary: "SEO tags",
    headSecondary: "Add/Manage SEO tags",
    component: <SeoTags />,
  },
  {
    panelName: "advanced-medicine-settings",
    headPrimary: "Medicine settings",
    headSecondary: "Manage Medicines",
    component: <AdvancedMedicineSettings />,
  },
  {
    panelName: "manage-brochure",
    headPrimary: "Brochures",
    headSecondary: "Handle Brochure/PDF",
    component: <ManageBrochure />,
  },
  {
    panelName: "manual-invoice",
    headPrimary: "Manual Invoice",
    headSecondary: "",
    component: <InvoiceVariantSelection />,
  },
];
